import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import CopyIconButton from '../CopyIconButton';
import { maskKeyString } from '../../utils/apiKey';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8090';

const DEFAULT_SAS_CODE = `proc sql;
    create table lib.filtered as
    select name, age
    from sashelp.class
    where age > 12;
quit;`;

function TestApiKeyTab() {
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [result, setResult] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    const handleConvert = async () => {
        if (!apiKey.trim()) return;
        setStatus('loading');
        setResult('');
        try {
            const response = await apiService.converterConvertCode(DEFAULT_SAS_CODE, apiKey);
            setResult(response.data.code);
            setStatus('success');
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Conversion failed.';
            setResult(errorMsg);
            setStatus('error');
        }
    };

    const curlCode = DEFAULT_SAS_CODE.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
    const buildCurl = (key: string) => `curl -X POST ${API_BASE_URL}/api/v1/converter/convert/code \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${key}" \\
  -d "{\\"code\\": \\"${curlCode}\\"}"`;
    const trimmedKey = apiKey.trim();
    // The text view masks the key. The copy button copies the plain key.
    const curlCmd = buildCurl(trimmedKey || '<API-KEY>');
    const curlCmdMasked = buildCurl(trimmedKey ? maskKeyString(trimmedKey) : '<API-KEY>');

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-secondary mb-1" htmlFor="demo-api-key">API Key</label>
                <div className="relative">
                    <input
                        id="demo-api-key"
                        type={showApiKey ? 'text' : 'password'}
                        autoComplete="off"
                        className="bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-3 pr-10 py-2 w-full font-mono"
                        placeholder="Enter your Alchemist API key"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-secondary hover:text-body-color transition-colors"
                        title={showApiKey ? 'Hide API key' : 'Show API key'}
                        aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                        {showApiKey ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary mb-1">SAS Code</label>
                <textarea
                    readOnly
                    className="font-mono bg-body-bg border border-gray-600 text-body-color rounded-md p-3 w-full h-64 resize-y"
                    value={DEFAULT_SAS_CODE}
                />
            </div>

            <div className="flex items-center space-x-3">
                <button
                    className="btn-primary"
                    onClick={handleConvert}
                    disabled={status === 'loading' || !apiKey.trim()}
                >
                    {status === 'loading' ? 'Converting...' : 'Convert'}
                </button>
                {status === 'success' && (
                    <span className="bg-primary/20 text-primary px-2 py-1 rounded text-xs font-medium">success</span>
                )}
                {status === 'error' && (
                    <span className="bg-red-600/20 text-red-400 px-2 py-1 rounded text-xs font-medium">error</span>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary mb-1">DBR Output</label>
                <textarea
                    readOnly
                    className="font-mono bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md p-3 w-full h-64 resize-y"
                    value={result}
                    placeholder="Converted code will appear here..."
                />
            </div>

            <div>
                <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium text-secondary">cURL Command</label>
                    <CopyIconButton text={curlCmd} label="cURL command" iconClassName="w-3.5 h-3.5" />
                </div>
                <textarea
                    readOnly
                    className="font-mono text-xs bg-body-bg border border-gray-600 text-body-color rounded-md p-3 w-full h-32 resize-y"
                    value={curlCmdMasked}
                />
            </div>
        </div>
    );
}

export default TestApiKeyTab;
